import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import {
  buildSandboxDefaultsFromSpec,
  buildSpecEnums,
  buildSpecRequired,
  buildUpdateFieldSet,
  PUT_RESOURCE_PATHS,
  isEditableField,
} from './sandboxConfig';
import { useFetch } from '../api';
import { createResource, updateResource, replaceEmptyStringsWithNull } from './SandboxApi';

const isScalar = (value) => value === null || value === undefined || typeof value !== 'object';

const formatFieldName = (key) => key
  .split('_')
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

const inferInputType = (key, value) => {
  if (typeof value === 'boolean') return 'checkbox';
  if (typeof value === 'number') return 'number';
  if (key.includes('date') || key.includes('_at')) return 'datetime-local';
  return 'text';
};

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

function RequiredMark() {
  return <span className="text-red-500 ml-0.5">*</span>;
}

function EnumField({
  fieldKey, value, onChange, options, required, disabled,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const inputRef = useRef(null);

  const filteredOptions = filter
    ? options.filter((opt) => opt.toLowerCase().includes(filter.toLowerCase()))
    : options;

  useEffect(() => {
    if (!inputRef.current) return;
    const isValid = !value || options.includes(String(value));
    inputRef.current.setCustomValidity(isValid ? '' : 'Must select a value from the list');
  }, [value, options]);

  const handleFocus = () => {
    setFilter('');
    setIsOpen(true);
  };

  const handleSelect = (opt) => {
    onChange(opt);
    setIsOpen(false);
    setFilter('');
  };

  const handleBlur = () => {
    setTimeout(() => setIsOpen(false), 150);
  };

  return (
    <div className={`relative${disabled ? ' opacity-50 pointer-events-none' : ''}`}>
      <div className="text-xs text-surface-500 mb-1">{formatFieldName(fieldKey)}{required && <RequiredMark />}</div>
      <input
        ref={inputRef}
        value={isOpen ? filter : (value ?? '')}
        onChange={(e) => setFilter(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="input text-sm"
        autoComplete="off"
      />
      {isOpen && (
        <div role="listbox" className="absolute z-10 w-full bg-white border border-surface-200 rounded-md shadow-lg max-h-48 overflow-y-auto mt-1">
          {filteredOptions.map((opt) => (
            <div
              key={opt}
              role="option"
              aria-selected={opt === value}
              tabIndex={0}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-surface-50 ${opt === value ? 'font-medium text-accent-600' : ''}`}
              onMouseDown={() => handleSelect(opt)}
            >
              {opt}
            </div>
          ))}
          {filteredOptions.length === 0 && (
            <div className="px-3 py-2 text-sm text-surface-400">No matches</div>
          )}
        </div>
      )}
    </div>
  );
}

function parseDatetime(value) {
  if (!value) return { date: '', time: '00:00' };
  const normalized = String(value).replace('Z', '').split('.')[0];
  const [date, timePart = ''] = normalized.split('T');
  const [h = '00', m = '00'] = timePart.split(':');
  return { date, time: `${h.padStart(2, '0')}:${m.padStart(2, '0')}` };
}

function DateTimeField({
  fieldKey, value, onChange, required, disabled,
}) {
  const [timeOpen, setTimeOpen] = useState(false);
  const { date, time } = parseDatetime(value);
  const [selHour, selMin] = time.split(':').map(Number);

  const emit = (d, t) => onChange(d ? `${d}T${t}` : '');

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    emit(newDate, time || '00:00');
  };

  const handleTimeSelect = (h, m) => {
    const t = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    emit(date, t);
    setTimeOpen(false);
  };

  return (
    <div className={disabled ? 'opacity-50 pointer-events-none' : ''}>
      <div className="text-xs text-surface-500 mb-1">{formatFieldName(fieldKey)}{required && <RequiredMark />}</div>
      <div className="flex gap-2">
        <input
          type="date"
          value={date}
          onChange={handleDateChange}
          className="input text-sm flex-1"
        />
        <div className="relative flex-1">
          <button
            type="button"
            onClick={() => setTimeOpen((o) => !o)}
            className="input text-sm w-full text-left tabular-nums"
          >
            {time}
          </button>
          {timeOpen && (
            <div className="absolute right-0 z-10 mt-1 bg-white border border-surface-200 rounded-md shadow-lg flex">
              <div className="flex flex-col">
                <div className="text-[10px] text-surface-500 text-center px-3 pt-2 pb-1 font-medium">HH</div>
                <div role="listbox" className="overflow-y-auto h-44 w-14">
                  {Array.from({ length: 24 }, (_, h) => (
                    <div
                      key={h}
                      role="option"
                      aria-selected={h === selHour}
                      tabIndex={0}
                      onMouseDown={() => handleTimeSelect(h, selMin)}
                      className={`text-center py-1 text-sm cursor-pointer ${h === selHour ? 'bg-accent-600 text-white' : 'hover:bg-surface-50'}`}
                    >
                      {String(h).padStart(2, '0')}
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-px bg-surface-100" />
              <div className="flex flex-col">
                <div className="text-[10px] text-surface-500 text-center px-3 pt-2 pb-1 font-medium">MM</div>
                <div role="listbox" className="overflow-y-auto h-44 w-14">
                  {Array.from({ length: 60 }, (_, m) => (
                    <div
                      key={m}
                      role="option"
                      aria-selected={m === selMin}
                      tabIndex={0}
                      onMouseDown={() => handleTimeSelect(selHour, m)}
                      className={`text-center py-1 text-sm cursor-pointer ${m === selMin ? 'bg-accent-600 text-white' : 'hover:bg-surface-50'}`}
                    >
                      {String(m).padStart(2, '0')}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ScalarField({
  fieldKey, value, onChange, options, required, disabled,
}) {
  const type = inferInputType(fieldKey, value);

  if (type === 'checkbox') {
    return (
      <div className={disabled ? 'opacity-50 pointer-events-none' : ''}>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className="text-xs text-surface-500 mb-1 flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded border-surface-300"
          />
          {formatFieldName(fieldKey)}{required && <RequiredMark />}
        </label>
      </div>
    );
  }

  if (options) {
    return (
      <EnumField
        fieldKey={fieldKey}
        value={value}
        onChange={onChange}
        options={options}
        required={required}
        disabled={disabled}
      />
    );
  }

  if (type === 'datetime-local') {
    return (
      <DateTimeField
        fieldKey={fieldKey}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
      />
    );
  }

  return (
    <div className={disabled ? 'opacity-50 pointer-events-none' : ''}>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label className="text-xs text-surface-500 mb-1 block">
        {formatFieldName(fieldKey)}{required && <RequiredMark />}
        <input
          type={type}
          value={value ?? ''}
          onChange={(e) => {
            let parsed = e.target.value;
            if (type === 'number') parsed = parsed === '' ? '' : Number(parsed);
            onChange(parsed);
          }}
          className="input text-sm mt-1"
        />
      </label>
    </div>
  );
}

const isScalarArray = (value) => Array.isArray(value) && value.every((v) => isScalar(v));

function ScalarArrayField({
  fieldKey, data, onChange, required, disabled,
}) {
  return (
    <div className={disabled ? 'opacity-50 pointer-events-none' : ''}>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label className="text-xs text-surface-500 mb-1 block">
        {formatFieldName(fieldKey)}{required && <RequiredMark />}
        <input
          type="text"
          value={(data || []).join(', ')}
          onChange={(e) => {
            const raw = e.target.value;
            onChange(raw === '' ? [] : raw.split(',').map((s) => s.trim()));
          }}
          placeholder="comma-separated values"
          className="input text-sm mt-1"
        />
      </label>
    </div>
  );
}

function NestedObjectSection({
  fieldKey, data, onChange, enums = {}, required,
  itemRequired = new Set(), disabled, allowedSubFields,
}) {
  const handleFieldChange = (subKey, value) => {
    onChange({ ...data, [subKey]: value });
  };

  return (
    <div className={`border border-surface-100 rounded-md p-3 space-y-2${disabled ? ' opacity-50 pointer-events-none' : ''}`}>
      <div className="text-xs font-medium text-surface-700">{formatFieldName(fieldKey)}{required && <RequiredMark />}</div>
      {Object.entries(data).map(([subKey, subValue]) => {
        if (!isScalar(subValue)) return null;
        return (
          <div key={subKey} className="pl-3">
            <ScalarField
              fieldKey={subKey}
              value={subValue}
              onChange={(v) => handleFieldChange(subKey, v)}
              options={enums[subKey]}
              required={itemRequired.has(subKey)}
              disabled={allowedSubFields ? !allowedSubFields.has(subKey) : false}
            />
          </div>
        );
      })}
    </div>
  );
}

function ArrayOfObjectsSection({
  fieldKey, data, onChange, enums = {}, required,
  itemRequired = new Set(), disabled, allowedSubFields,
}) {
  const handleItemFieldChange = (index, subKey, value) => {
    const updated = deepClone(data);
    updated[index][subKey] = value;
    onChange(updated);
  };

  const handleAddItem = () => {
    // clone first item as template with empty values
    const template = {};
    const sample = data[0] || {};
    Object.entries(sample).forEach(([k, v]) => {
      if (typeof v === 'boolean') template[k] = false;
      else if (typeof v === 'number') template[k] = 0;
      else template[k] = '';
    });
    onChange([...data, template]);
  };

  const handleRemoveItem = (index) => {
    onChange(data.filter((_, i) => i !== index));
  };

  return (
    <div className={`border border-surface-100 rounded-md p-3 space-y-3${disabled ? ' opacity-50 pointer-events-none' : ''}`}>
      <div className="text-xs font-medium text-surface-700">{formatFieldName(fieldKey)}{required && <RequiredMark />}</div>
      {data.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} className="pl-3 space-y-2 border-l-2 border-surface-100 ml-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium text-surface-400 uppercase">Item #{index + 1}</span>
            {!disabled && data.length > 1 && (
              <button
                type="button"
                className="text-[10px] text-red-500 hover:text-red-700"
                onClick={() => handleRemoveItem(index)}
              >
                Remove
              </button>
            )}
          </div>
          {Object.entries(item).map(([subKey, subValue]) => {
            if (!isScalar(subValue)) return null;
            return (
              <div key={subKey} className="pl-2">
                <ScalarField
                  fieldKey={subKey}
                  value={subValue}
                  onChange={(v) => handleItemFieldChange(index, subKey, v)}
                  options={enums[subKey]}
                  required={itemRequired.has(subKey)}
                  disabled={allowedSubFields ? !allowedSubFields.has(subKey) : false}
                />
              </div>
            );
          })}
        </div>
      ))}
      {!disabled && (
        <button
          type="button"
          className="text-xs text-accent-600 hover:text-accent-700 font-medium pl-3"
          onClick={handleAddItem}
        >
          + Add Item
        </button>
      )}
    </div>
  );
}

function ResourceEditorModal({
  isOpen, onClose, connectionId, sandboxType, resourceKey, apiPath,
  existingResource, onSuccess, onError, openApiSpec,
}) {
  const trackstar = useFetch();
  const [formData, setFormData] = useState({});
  const [nestedData, setNestedData] = useState({});
  const [loading, setLoading] = useState(false);

  const isUpdate = !!existingResource;

  useEffect(() => {
    if (!isOpen) return;

    const specDefaults = buildSandboxDefaultsFromSpec(openApiSpec);
    const source = isUpdate ? existingResource : (specDefaults[resourceKey] ?? {});
    const scalars = {};
    const nested = {};

    Object.entries(source).forEach(([key, value]) => {
      if (!isEditableField(key)) return;
      if (isScalar(value)) {
        scalars[key] = value;
      } else {
        nested[key] = deepClone(value);
      }
    });

    setFormData(scalars);
    setNestedData(nested);
  }, [isOpen, existingResource, sandboxType, resourceKey, openApiSpec]);

  const allowedFields = useMemo(
    () => (isUpdate ? buildUpdateFieldSet(openApiSpec, resourceKey) : null),
    [isUpdate, openApiSpec, resourceKey],
  );

  const fieldEnums = useMemo(() => {
    const path = isUpdate ? PUT_RESOURCE_PATHS[resourceKey] : `/${apiPath}`;
    const method = isUpdate ? 'put' : 'post';
    return buildSpecEnums(openApiSpec, path, method);
  }, [openApiSpec, apiPath, resourceKey, isUpdate]);

  const requiredInfo = useMemo(() => {
    const path = isUpdate ? PUT_RESOURCE_PATHS[resourceKey] : `/${apiPath}`;
    const method = isUpdate ? 'put' : 'post';
    return buildSpecRequired(openApiSpec, path, method);
  }, [openApiSpec, apiPath, resourceKey, isUpdate]);

  if (!isOpen) return null;

  const handleScalarChange = (key, value, type) => {
    let parsed = value;
    if (type === 'number') parsed = value === '' ? '' : Number(value);
    if (type === 'checkbox') parsed = value;
    setFormData({ ...formData, [key]: parsed });
  };

  const handleNestedChange = (key, value) => {
    setNestedData({ ...nestedData, [key]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const merged = { ...formData, ...nestedData };
    const body = allowedFields
      ? Object.fromEntries(Object.entries(merged).filter(([k]) => allowedFields.fields.has(k)))
      : merged;
    // datetime-local inputs strip timezone; re-append UTC
    Object.entries(body).forEach(([key, value]) => {
      if (typeof value === 'string' && inferInputType(key, value) === 'datetime-local') {
        if (value && !value.endsWith('Z') && !value.includes('+')) {
          body[key] = `${value}:00.000Z`;
        }
      }
    });
    replaceEmptyStringsWithNull(body);

    const promise = isUpdate
      ? updateResource(trackstar, connectionId, apiPath, existingResource.id, body)
      : createResource(trackstar, connectionId, apiPath, body);

    promise
      .then(() => {
        setLoading(false);
        onSuccess();
        onClose();
      })
      .catch((err) => {
        setLoading(false);
        onError(String(err));
      });
  };

  const scalarEntries = Object.entries(formData).filter(([key]) => isEditableField(key));
  const nestedEntries = Object.entries(nestedData);
  const resourceLabel = formatFieldName(resourceKey.replace(/-/g, '_')).replace(/s$/, '');

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="modal-backdrop" onClick={onClose} aria-hidden="true" />
      <form className="modal max-w-lg max-h-[85vh]" onSubmit={handleSubmit}>
        <div className="modal-header">
          <h2 className="text-lg font-semibold text-surface-900">
            {isUpdate ? 'Update' : 'Create'}
            {' '}
            {resourceLabel}
          </h2>
        </div>

        <div className="modal-body space-y-3">
          {!isUpdate && !openApiSpec && (
            <div className="flex justify-center py-6">
              <ClipLoader size={24} color="#D4532A" />
            </div>
          )}
          {scalarEntries.map(([key, value]) => (
            <ScalarField
              key={key}
              fieldKey={key}
              value={value}
              onChange={(v) => {
                const type = inferInputType(key, value);
                handleScalarChange(key, v, type);
              }}
              options={Array.isArray(fieldEnums[key]) ? fieldEnums[key] : undefined}
              required={requiredInfo.fields.has(key)}
              disabled={allowedFields ? !allowedFields.fields.has(key) : false}
            />
          ))}

          {nestedEntries.length > 0 && <hr className="divider" />}

          {nestedEntries.map(([key, value]) => {
            const nestedEnums = typeof fieldEnums[key] === 'object' && !Array.isArray(fieldEnums[key]) ? fieldEnums[key] : {};
            const isReq = requiredInfo.fields.has(key);
            const itemRequired = requiredInfo.nested[key] ?? new Set();
            const isDisabled = allowedFields ? !allowedFields.fields.has(key) : false;
            const allowedSubFields = allowedFields?.nested[key] ?? null;
            if (isScalarArray(value)) {
              return (
                <ScalarArrayField
                  key={key}
                  fieldKey={key}
                  data={value}
                  onChange={(v) => handleNestedChange(key, v)}
                  required={isReq}
                  disabled={isDisabled}
                />
              );
            }
            if (Array.isArray(value)) {
              return (
                <ArrayOfObjectsSection
                  key={key}
                  fieldKey={key}
                  data={value}
                  onChange={(v) => handleNestedChange(key, v)}
                  enums={nestedEnums}
                  required={isReq}
                  itemRequired={itemRequired}
                  disabled={isDisabled}
                  allowedSubFields={allowedSubFields}
                />
              );
            }
            return (
              <NestedObjectSection
                key={key}
                fieldKey={key}
                data={value}
                onChange={(v) => handleNestedChange(key, v)}
                enums={nestedEnums}
                required={isReq}
                itemRequired={itemRequired}
                disabled={isDisabled}
                allowedSubFields={allowedSubFields}
              />
            );
          })}
        </div>

        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {loading ? 'Saving...' : (isUpdate ? 'Update' : 'Create')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ResourceEditorModal;
